<template>
  <div
    v-if="visible"
    class="ml-passages-panel"
    :class="{ 'ml-panel--new-detection': newDetection }"
  >
    <!-- ─── Header ─────────────────────────────────────────── -->
    <div class="ml-panel-header">
      <div class="row items-center no-wrap q-gutter-x-sm">
        <q-icon name="psychology" color="white" size="18px" />
        <span class="text-subtitle2 text-white text-bold"
          >ML Verified Passages</span
        >
        <div class="col row items-center no-wrap q-gutter-x-sm q-mx-sm">
          <div
            class="text-caption text-white"
            style="opacity: 0.8; white-space: nowrap"
          >
            &gt;{{ mlDisplayThreshold }}%
          </div>
          <q-slider
            v-model="mlDisplayThreshold"
            :min="50"
            :max="99"
            :step="1"
            color="white"
            track-color="blue-grey-5"
            class="col"
            dense
            @change="onThresholdChange"
          />
        </div>
        <q-btn
          v-if="currentPassage"
          flat
          dense
          round
          icon="place"
          color="white"
          size="sm"
          title="Zoom to Pig"
          @click="onZoomToPig"
        />
        <q-btn
          flat
          dense
          round
          icon="refresh"
          color="white"
          size="sm"
          :loading="loading"
          title="Reload"
          @click="loadMLPassages(false)"
        />
        <q-btn
          flat
          dense
          round
          icon="close"
          color="white"
          size="sm"
          title="Close"
          aria-label="Close ML Verified Passages"
          data-testid="ml-panel-close"
          @click="$emit('close')"
        />
      </div>
    </div>

    <!-- ─── Body ───────────────────────────────────────────── -->
    <div class="ml-panel-body">
      <!-- Loading state (only on first load when no data to show) -->
      <div
        v-if="loading && enrichedPassages.length === 0"
        class="column items-center justify-center q-pa-lg"
      >
        <q-spinner-dots color="primary" size="40px" />
        <div class="text-caption q-mt-sm text-grey-7">Fetching passages…</div>
      </div>

      <!-- No pig run selected -->
      <div
        v-else-if="!selectedPigRunEntry"
        class="column items-center justify-center q-pa-lg text-center"
      >
        <q-icon name="route" color="grey-5" size="40px" />
        <div class="text-body2 q-mt-sm text-grey-7">
          No pig run is currently selected.
        </div>
        <div class="text-caption text-grey-5 q-mt-xs">
          Select a pig run using the map layer control above.
        </div>
      </div>

      <!-- No high-confidence passages -->
      <div
        v-else-if="!loading && enrichedPassages.length === 0"
        class="column items-center justify-center q-pa-lg text-center"
      >
        <q-icon name="search_off" color="grey-5" size="40px" />
        <div class="text-body2 q-mt-sm text-grey-7">
          No ML passages above {{ mlDisplayThreshold }}% found.
        </div>
        <div class="text-caption text-grey-5 q-mt-xs">
          High-confidence passaged will appear here when detected.
        </div>
      </div>

      <!-- Passage detail -->
      <template v-else-if="currentPassage">
        <!-- Background refresh indicator -->
        <q-linear-progress
          v-if="loading"
          indeterminate
          color="primary"
          size="2px"
        />
        <!-- Row 1: nav ◄ · breadcrumb · ► -->
        <div class="row items-center no-wrap q-px-xs q-pt-xs">
          <q-btn
            flat
            dense
            round
            icon="chevron_left"
            color="primary"
            size="sm"
            :disable="prevMarkerFirstIndex === -1"
            @click="onPrevPassage"
          />
          <div class="col text-center">
            <div class="row items-center justify-center no-wrap q-gutter-x-xs">
              <span class="text-caption text-grey-5">Pig Run</span>
              <q-btn
                flat
                dense
                no-caps
                size="sm"
                class="text-caption text-grey-6 ml-breadcrumb-btn"
              >
                {{ currentPassage.pigRunName }}
                <q-icon name="arrow_drop_down" size="16px" class="q-ml-none" />
                <q-menu>
                  <q-list dense style="min-width: 150px">
                    <q-item
                      v-for="opt in pigRunOptions"
                      :key="opt.value"
                      v-close-popup
                      clickable
                      :active="opt.value === selectedPigRunId"
                      @click="onSelectPigRun(opt.value)"
                    >
                      <q-item-section>{{ opt.label }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
              <q-icon name="chevron_right" size="12px" color="grey-5" />
              <span class="text-caption text-grey-5">Marker</span>
              <q-btn
                flat
                dense
                no-caps
                size="sm"
                class="text-caption text-grey-9 text-bold ml-breadcrumb-btn"
              >
                {{ currentPassage.markerName }}
                <q-icon name="arrow_drop_down" size="16px" class="q-ml-none" />
                <q-menu>
                  <q-list dense style="min-width: 120px">
                    <q-item
                      v-for="opt in markerOptions"
                      :key="opt.value"
                      v-close-popup
                      clickable
                      :active="
                        opt.value === currentPassage?.pipelineSectionMarkerId
                      "
                      @click="onSelectMarker(opt.value)"
                    >
                      <q-item-section>{{ opt.label }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
            <div class="text-caption text-grey-5" style="font-size: 10px">
              Passage {{ currentIndex + 1 }} of {{ enrichedPassages.length }}
              <template v-if="passagesAtCurrentMarker.total > 1">
                &mdash;
                <q-btn
                  flat
                  dense
                  round
                  icon="chevron_left"
                  size="xs"
                  color="primary"
                  :disable="prevPassageAtMarkerIndex === -1"
                  @click="onPrevPassageAtMarker" />
                {{ passagesAtCurrentMarker.index }}/{{
                  passagesAtCurrentMarker.total
                }}
                at marker
                <q-btn
                  flat
                  dense
                  round
                  icon="chevron_right"
                  size="xs"
                  color="primary"
                  :disable="nextPassageAtMarkerIndex === -1"
                  @click="onNextPassageAtMarker"
              /></template>
            </div>
          </div>
          <q-btn
            flat
            dense
            round
            icon="chevron_right"
            color="primary"
            size="sm"
            :disable="nextMarkerFirstIndex === -1"
            @click="onNextPassage"
          />
        </div>

        <!-- Row 2: time · UID · ML score -->
        <div class="row items-center q-px-sm q-pb-xs q-gutter-x-sm">
          <div class="col">
            <div class="text-caption text-grey-6">Time (UTC)</div>
            <div class="text-caption text-bold">
              {{ formatTimestamp(currentPassage.passageTimeStamp) }}
            </div>
          </div>
          <div class="col-auto">
            <div class="text-caption text-grey-6">UID</div>
            <div class="text-caption text-bold">
              {{ currentPassage.uid ?? '—' }}
            </div>
          </div>
          <div class="col-auto column items-end">
            <div class="text-caption text-grey-6">ML</div>
            <q-badge
              v-for="wf in mlWaveforms(currentPassage)"
              :key="wf.passageWaveformId"
              :color="
                getMLColor(wf.mlPrediction ?? false, wf.mlConfidence ?? 0)
              "
            >
              {{
                getMLConfidenceLabel(
                  wf.mlPrediction ?? false,
                  wf.mlConfidence ?? 0
                )
              }}
              {{ getWaveformTypeLabel(wf.passageWaveformType) }}
            </q-badge>
          </div>
        </div>

        <!-- Row 3: waveform canvases -->
        <div class="q-px-sm q-pb-xs">
          <div v-if="waveformLoading" class="row justify-center q-pa-sm">
            <q-spinner size="24px" color="primary" />
          </div>
          <div
            v-else-if="decodedWaveforms.length === 0"
            class="text-caption text-grey-5 q-py-xs text-center"
          >
            No waveform data available
          </div>
          <template v-else>
            <div
              v-if="
                canVerify &&
                !isCurrentPassageVerified &&
                decodedWaveforms.length > 1
              "
              class="text-caption text-grey-6 q-pb-xs text-center"
            >
              Click a waveform to select it, then press Verify
            </div>
            <div class="row q-gutter-md justify-center ml-waveform-row">
              <div
                v-for="wf in decodedWaveforms"
                :key="wf.passageWaveformId"
                class="ml-waveform-card"
                :class="{
                  'ml-waveform-selected':
                    selectedWaveformId === wf.passageWaveformId,
                  'ml-waveform-selectable':
                    canVerify && !isCurrentPassageVerified,
                }"
                @click="
                  canVerify &&
                    !isCurrentPassageVerified &&
                    (selectedWaveformId = wf.passageWaveformId)
                "
              >
                <div class="text-caption text-center text-bold q-mb-xs">
                  {{ getWaveformTypeLabel(wf.passageWaveformType) }}
                </div>
                <canvas
                  :ref="'mlCanvas_' + wf.passageWaveformId"
                  width="160"
                  height="90"
                  class="ml-waveform-canvas"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- Row 4: go-to-verify-next -->
        <div class="row q-px-sm q-pb-xs q-gutter-x-sm">
          <q-btn
            unelevated
            dense
            color="blue-grey-7"
            icon="skip_next"
            label="Last Verified"
            class="col"
            style="font-size: 11px"
            :disable="goToLastVerifiedIndex === -1"
            @click="onGoToLastVerified"
          >
            <q-tooltip max-width="220px">
              Jump directly to the most recently verified passage
            </q-tooltip>
          </q-btn>
          <q-btn
            unelevated
            dense
            color="blue-grey-7"
            icon="skip_next"
            label="Newest"
            class="col"
            style="font-size: 11px"
            :disable="goToVerifyNextIndex === -1"
            @click="onGoToVerifyNext"
          >
            <q-tooltip max-width="220px">
              This will take you to the first marker that follows the marker
              with the last verified passage
            </q-tooltip>
          </q-btn>
        </div>

        <!-- Row 5: action buttons -->
        <div class="row q-px-sm q-pb-sm q-gutter-x-sm">
          <q-btn
            unelevated
            dense
            color="primary"
            icon="my_location"
            label="Go to Marker"
            class="col"
            style="font-size: 11px"
            @click="onGoToMarker"
          />
          <q-btn
            v-if="canVerify"
            unelevated
            dense
            :color="isCurrentPassageVerified ? 'green' : 'orange'"
            :icon="isCurrentPassageVerified ? 'check_circle' : 'fact_check'"
            :label="isCurrentPassageVerified ? 'Unverify' : 'Verify Waveform'"
            class="col"
            style="font-size: 11px"
            @click="onVerify"
          />
        </div>
      </template>
    </div>

    <!-- Sub-components used beyond visible area -->
    <PipelineSectionMarkerPassagesDialog ref="markerPassagesDialog" />
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import DeviceService from '@/services/DeviceService';
import PigRunService from '@/services/PigRunService';
import PipelineSectionMarkerPassagesDialog from '@/components/PipelineSectionMarkerPassagesDialog.vue';
import { IPassage } from '@/models/Passage';
import { IMLPassageResult } from '@/models/Passage';
import {
  IPassageWaveformSummary,
  IDecodedWaveform,
  IWaveformApiResponse,
  WaveformTypeLabel,
} from '@/models/PassageWaveform';
import { PigRunMapEntry } from '@/models/PigRunState';
import { decodeWaveforms, isAnyWaveformVerified } from '@/utils/WaveformUtils';
import { validatePassageVerification } from '@/utils/PassageValidation';
import { EventBus } from '@/utils/EventBus';

// ML threshold: slider 50–100 maps directly to mlConfidence [0.5, 1.0]
// Default: 98 → sends mlConfidenceThreshold 0.98
const ML_CONFIDENCE_THRESHOLD_DEFAULT_DISPLAY = 98;

// IMLPassageResult already has all marker/pigRun fields returned by the
// backend. We only extend it with a pig run name that we resolve client-
// side from the Vuex store (avoids sending the name from the server).
interface EnrichedMLPassage extends IMLPassageResult {
  pigRunName: string;
}

export default defineComponent({
  name: 'MLPassagesPanel',
  components: {
    PipelineSectionMarkerPassagesDialog,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      waveformLoading: false,
      enrichedPassages: [] as EnrichedMLPassage[],
      currentIndex: 0,
      decodedWaveforms: [] as IDecodedWaveform[],
      selectedWaveformId: null as number | null,
      unsubscribeAction: null as (() => void) | null,
      debounceTimer: null as ReturnType<typeof setTimeout> | null,
      refreshMaxWaitTimer: null as ReturnType<typeof setTimeout> | null,
      newDetection: false,
      detectionResetTimer: null as ReturnType<typeof setTimeout> | null,
      mlDisplayThreshold: ML_CONFIDENCE_THRESHOLD_DEFAULT_DISPLAY,
    };
  },
  computed: {
    mlConfidenceThreshold(): number {
      // Slider range 50–100 maps directly to confidence range [0.5, 1.0]
      return this.mlDisplayThreshold / 100;
    },
    pigRunEntries(): PigRunMapEntry[] {
      const pigRuns = this.$store.state.pigRun.pigRuns as Record<
        number,
        PigRunMapEntry
      >;
      const visibleIds = this.$store.state.pigRun.visiblePigRunIds as number[];
      return visibleIds
        .filter((id: number) => pigRuns[id])
        .map((id: number) => pigRuns[id]);
    },
    selectedPigRunId(): number | null {
      return this.$store.state.pigRun.selectedPigRunId;
    },
    selectedPigRunEntry(): PigRunMapEntry | null {
      const selectedId = this.selectedPigRunId;
      if (selectedId == null) return null;
      return (
        (this.$store.state.pigRun.pigRuns as Record<number, PigRunMapEntry>)[
          selectedId
        ] ?? null
      );
    },
    pigRunOptions(): Array<{ label: string; value: number }> {
      return this.pigRunEntries.map((e) => ({
        label: this.pigRunLabel(e),
        value: e.pigRun.pigRunId,
      }));
    },
    markerOptions(): Array<{ label: string; value: number }> {
      const seen = new Set<number>();
      const options: Array<{ label: string; value: number }> = [];
      for (const p of this.enrichedPassages) {
        if (!seen.has(p.pipelineSectionMarkerId)) {
          seen.add(p.pipelineSectionMarkerId);
          options.push({
            label: p.markerName,
            value: p.pipelineSectionMarkerId,
          });
        }
      }
      return options;
    },
    currentPassage(): EnrichedMLPassage | null {
      if (this.enrichedPassages.length === 0) return null;
      return this.enrichedPassages[this.currentIndex] ?? null;
    },
    canVerify(): boolean {
      const role = this.$store.state.user?.role ?? '';
      return role === 'admin' || role === 'superadmin';
    },
    isCurrentPassageVerified(): boolean {
      if (!this.currentPassage) return false;
      return isAnyWaveformVerified(this.currentPassage);
    },
    /**
     * Index of the first passage that belongs to the marker immediately
     * following the marker that contains the last verified passage.
     * Returns -1 if no verified passage exists or there is no following marker.
     */
    goToLastVerifiedIndex(): number {
      const passages = this.enrichedPassages;
      for (let i = passages.length - 1; i >= 0; i--) {
        if (isAnyWaveformVerified(passages[i])) {
          return i;
        }
      }
      return -1;
    },
    /**
     * Position of the current passage within the group of passages that share
     * the same marker, and the total count of passages in that group.
     */
    passagesAtCurrentMarker(): { index: number; total: number } {
      const passage = this.currentPassage;
      if (!passage) return { index: 0, total: 0 };
      const group = this.enrichedPassages.filter(
        (p) =>
          p.markerSortOrder === passage.markerSortOrder &&
          p.pigRunId === passage.pigRunId
      );
      const localIdx = group.findIndex(
        (p) => p.passageId === passage.passageId
      );
      return { index: localIdx + 1, total: group.length };
    },
    prevMarkerFirstIndex(): number {
      const passage = this.currentPassage;
      if (!passage) return -1;
      // Find the start of the current marker group
      const groupStart = this.enrichedPassages.findIndex(
        (p) =>
          p.markerSortOrder === passage.markerSortOrder &&
          p.pigRunId === passage.pigRunId
      );
      if (groupStart <= 0) return -1;
      // Entry just before the current group belongs to the previous marker
      const prevMarker = this.enrichedPassages[groupStart - 1];
      // First passage of that previous marker
      return this.enrichedPassages.findIndex(
        (p) =>
          p.markerSortOrder === prevMarker.markerSortOrder &&
          p.pigRunId === prevMarker.pigRunId
      );
    },
    nextMarkerFirstIndex(): number {
      const passage = this.currentPassage;
      if (!passage) return -1;
      // Walk forward from currentIndex to find where the current group ends
      let lastInGroup = this.currentIndex;
      for (
        let i = this.currentIndex + 1;
        i < this.enrichedPassages.length;
        i++
      ) {
        const p = this.enrichedPassages[i];
        if (
          p.markerSortOrder === passage.markerSortOrder &&
          p.pigRunId === passage.pigRunId
        ) {
          lastInGroup = i;
        } else {
          break;
        }
      }
      const next = lastInGroup + 1;
      return next < this.enrichedPassages.length ? next : -1;
    },
    prevPassageAtMarkerIndex(): number {
      const passage = this.currentPassage;
      if (!passage) return -1;
      for (let i = this.currentIndex - 1; i >= 0; i--) {
        const p = this.enrichedPassages[i];
        if (
          p.markerSortOrder === passage.markerSortOrder &&
          p.pigRunId === passage.pigRunId
        )
          return i;
      }
      return -1;
    },
    nextPassageAtMarkerIndex(): number {
      const passage = this.currentPassage;
      if (!passage) return -1;
      for (
        let i = this.currentIndex + 1;
        i < this.enrichedPassages.length;
        i++
      ) {
        const p = this.enrichedPassages[i];
        if (
          p.markerSortOrder === passage.markerSortOrder &&
          p.pigRunId === passage.pigRunId
        )
          return i;
      }
      return -1;
    },
    goToVerifyNextIndex(): number {
      const passages = this.enrichedPassages;
      // Walk backwards to find the last verified passage
      let lastVerifiedSortOrder = -1;
      for (let i = passages.length - 1; i >= 0; i--) {
        if (isAnyWaveformVerified(passages[i])) {
          lastVerifiedSortOrder = passages[i].markerSortOrder;
          break;
        }
      }
      if (lastVerifiedSortOrder === -1) return -1;
      // Find the first passage whose markerSortOrder is strictly greater
      const idx = passages.findIndex(
        (p) => p.markerSortOrder > lastVerifiedSortOrder
      );
      return idx;
    },
  },
  watch: {
    visible(val: boolean) {
      if (val) {
        this.loadMLPassages();
        this.subscribeToSignalR();
      } else {
        this.cleanup();
      }
    },
    // Re-fetch when the selected pig run changes while the panel is open
    selectedPigRunId(newId: number | null, oldId: number | null) {
      if (!this.visible || newId === oldId) return;
      this.loadMLPassages();
    },
  },
  mounted() {
    EventBus.on('passage-verified', this.handlePassageVerified as any);
  },
  beforeUnmount() {
    EventBus.off('passage-verified', this.handlePassageVerified as any);
    this.cleanup();
  },
  methods: {
    // ─── Data loading ────────────────────────────────────────────────

    async loadMLPassages(preservePosition = false) {
      // Remember which passage the user is on so we can restore after refresh.
      const previousPassageId = preservePosition
        ? this.currentPassage?.passageId ?? null
        : null;

      this.loading = true;
      // Keep existing passages visible during background refresh to prevent resize
      if (!preservePosition) {
        this.enrichedPassages = [];
      }
      // Don't clear decodedWaveforms yet — wait until we know if the passage
      // actually changed, so waveforms can be preserved on a background refresh.

      const entry = this.selectedPigRunEntry;
      if (!entry) {
        this.enrichedPassages = [];
        this.decodedWaveforms = [];
        this.selectedWaveformId = null;
        this.currentIndex = 0;
        this.loading = false;
        return;
      }

      // Build a lookup from pigRunId → display name (resolved client-side)
      const pigRunNameById: Record<number, string> = {
        [entry.pigRun.pigRunId]: this.pigRunLabel(entry),
      };

      try {
        const raw = await PigRunService.getMLPassages({
          pigRunIds: [entry.pigRun.pigRunId],
          mlConfidenceThreshold: this.mlConfidenceThreshold,
        });

        // Attach pig run names resolved from the store, then sort:
        // markerSortOrder ASC → passageTimeStamp ASC within each marker
        const enriched: EnrichedMLPassage[] = raw.map((p) => ({
          ...p,
          pigRunName: pigRunNameById[p.pigRunId] ?? `Run ${p.pigRunId}`,
        }));

        enriched.sort((a, b) => {
          if (a.markerSortOrder !== b.markerSortOrder) {
            return a.markerSortOrder - b.markerSortOrder;
          }
          return a.passageTimeStamp.localeCompare(b.passageTimeStamp);
        });

        this.enrichedPassages = enriched;
      } catch (err) {
        console.error('MLPassagesPanel: failed to fetch ML passages', err);
      }

      // Restore position by passageId, or fall back to 0
      if (previousPassageId != null) {
        const restoredIdx = this.enrichedPassages.findIndex(
          (p) => p.passageId === previousPassageId
        );
        this.currentIndex =
          restoredIdx !== -1
            ? restoredIdx
            : Math.min(
                this.currentIndex,
                Math.max(0, this.enrichedPassages.length - 1)
              );
      } else {
        this.currentIndex = 0;
      }

      this.loading = false;

      if (this.enrichedPassages.length > 0) {
        // Only reload waveforms when the passage actually changed.
        const samePassage =
          previousPassageId != null &&
          this.enrichedPassages[this.currentIndex]?.passageId ===
            previousPassageId;
        if (!samePassage) {
          this.decodedWaveforms = [];
          this.selectedWaveformId = null;
          await this.loadCurrentPassageWaveforms();
        } else {
          // Same passage — waveform data is still valid, but the canvas
          // elements were unmounted during the loading spinner and are now
          // freshly mounted blank DOM nodes. Re-draw without re-fetching.
          await nextTick();
          this.renderAllCanvases();
        }
      } else {
        this.decodedWaveforms = [];
        this.selectedWaveformId = null;
      }
    },

    async loadCurrentPassageWaveforms() {
      this.decodedWaveforms = [];
      const passage = this.currentPassage;
      if (!passage || !passage.uid || !passage.passageWaveforms?.length) {
        return;
      }
      const firstWfId = passage.passageWaveforms[0].passageWaveformId;
      this.waveformLoading = true;
      try {
        const response = await DeviceService.getWaveformData(
          passage.uid,
          firstWfId
        );
        if (!response.data) return;
        const apiData: IWaveformApiResponse = response.data;
        this.decodedWaveforms = decodeWaveforms(apiData.passageWaveforms);
        // Only pre-select a waveform if the passage is already verified —
        // that way the verified waveform is highlighted. For unverified
        // passages the user must explicitly choose which waveform to verify.
        const verifiedWf = this.decodedWaveforms.find((wf) => wf.isVerified);
        this.selectedWaveformId = verifiedWf?.passageWaveformId ?? null;
        // Must set waveformLoading = false BEFORE nextTick so the v-else canvas
        // block mounts into the DOM prior to renderAllCanvases() reading refs.
        this.waveformLoading = false;
        await nextTick();
        this.renderAllCanvases();
      } catch (err) {
        console.error('MLPassagesPanel: failed to fetch waveform data', err);
        this.waveformLoading = false;
      }
    },

    // ─── Canvas rendering ────────────────────────────────────────────

    renderAllCanvases() {
      for (const wf of this.decodedWaveforms) {
        const refKey = 'mlCanvas_' + wf.passageWaveformId;
        const refs = this.$refs[refKey] as HTMLCanvasElement[] | undefined;
        const canvas = refs?.[0];
        if (canvas && wf.data?.length) {
          this.renderCanvas(canvas, wf.data);
        }
      }
    },

    renderCanvas(canvas: HTMLCanvasElement, samples: number[]) {
      const ctx = canvas.getContext('2d');
      if (!ctx || samples.length === 0) return;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, w, h);

      let min = samples[0];
      let max = samples[0];
      for (let i = 1; i < samples.length; i++) {
        if (samples[i] < min) min = samples[i];
        if (samples[i] > max) max = samples[i];
      }
      const range = max - min || 1;
      const padding = 4;
      const plotH = h - padding * 2;
      const plotW = w - padding * 2;
      const step = Math.max(1, Math.floor(samples.length / plotW));

      ctx.beginPath();
      ctx.strokeStyle = '#24337a';
      ctx.lineWidth = 1;
      let first = true;
      for (let i = 0; i < samples.length; i += step) {
        const x = padding + (i / (samples.length - 1)) * plotW;
        const y = padding + plotH - ((samples[i] - min) / range) * plotH;
        if (first) {
          ctx.moveTo(x, y);
          first = false;
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    },

    // ─── Navigation ───────────────────────────────────────────────────

    onPrevPassage() {
      const idx = this.prevMarkerFirstIndex;
      if (idx === -1) return;
      this.currentIndex = idx;
      this.onPassageChanged();
    },

    onNextPassage() {
      const idx = this.nextMarkerFirstIndex;
      if (idx === -1) return;
      this.currentIndex = idx;
      this.onPassageChanged();
    },

    onPassageChanged() {
      this.decodedWaveforms = [];
      this.selectedWaveformId = null;
      this.loadCurrentPassageWaveforms();
    },

    onPrevPassageAtMarker() {
      const idx = this.prevPassageAtMarkerIndex;
      if (idx === -1) return;
      this.currentIndex = idx;
      this.onPassageChanged();
    },

    onNextPassageAtMarker() {
      const idx = this.nextPassageAtMarkerIndex;
      if (idx === -1) return;
      this.currentIndex = idx;
      this.onPassageChanged();
    },

    onSelectPigRun(pigRunId: number) {
      this.$store.dispatch('selectPigRunLayer', pigRunId);
    },

    onSelectMarker(pipelineSectionMarkerId: number) {
      const idx = this.enrichedPassages.findIndex(
        (p) => p.pipelineSectionMarkerId === pipelineSectionMarkerId
      );
      if (idx !== -1) {
        this.currentIndex = idx;
        this.onPassageChanged();
      }
    },

    // ─── Actions ──────────────────────────────────────────────────────

    onGoToLastVerified() {
      const idx = this.goToLastVerifiedIndex;
      if (idx === -1) return;
      this.currentIndex = idx;
      this.onPassageChanged();
    },

    onGoToVerifyNext() {
      const idx = this.goToVerifyNextIndex;
      if (idx === -1) return;
      this.currentIndex = idx;
      this.onPassageChanged();
    },

    onZoomToPig() {
      const passage = this.currentPassage;
      if (!passage) return;
      const entry = this.pigRunEntries.find(
        (e) => e.pigRun.pigRunId === passage.pigRunId
      );
      const pigLoc = (entry as any)?.pigLocation;
      if (pigLoc?.lat && pigLoc?.lon) {
        this.$store.dispatch('goToLatLon', {
          lat: pigLoc.lat,
          lon: pigLoc.lon,
        });
        return;
      }
      // Fallback: zoom to the current passage's marker
      this.$store.dispatch('goToLatLon', {
        lat: passage.markerLat,
        lon: passage.markerLon,
      });
    },

    onThresholdChange() {
      this.loadMLPassages(false);
    },

    onGoToMarker() {
      const passage = this.currentPassage;
      if (!passage) return;
      this.$store.dispatch('goToLatLon', {
        lat: passage.markerLat,
        lon: passage.markerLon,
      });
      nextTick(() => {
        (this.$refs.markerPassagesDialog as any)?.openNew(
          passage.pipelineSectionMarkerId,
          passage.markerName,
          passage.pigRunId
        );
      });
    },

    async onVerify() {
      const passage = this.currentPassage;
      if (!passage || !this.canVerify) return;

      // Toggle: unverify if already verified
      if (isAnyWaveformVerified(passage)) {
        this.$store.dispatch('unverifyAllPassageWaveforms', { passage });
        EventBus.emit('passage-verified', { passage });
        return;
      }

      // Check verified passage and timestamp ordering
      const check = await validatePassageVerification(
        passage.pipelineSectionMarkerId,
        passage.pigRunId,
        passage.passageTimeStamp
      );
      if (!check.valid) {
        this.$q.notify({
          type: check.warning ? 'warning' : 'negative',
          timeout: check.warning ? 0 : undefined,
          actions: check.warning
            ? [{ icon: 'close', color: 'white', round: true }]
            : undefined,
          message: (check.warning ?? check.error)!,
        });
        return;
      }

      // Verify using whichever waveform card is selected
      const wfId = this.selectedWaveformId;
      if (wfId == null) return;
      this.$store.dispatch('verifyPassage', {
        passage,
        passageWaveformId: wfId,
      });
      EventBus.emit('passage-verified', { passage });
    },

    handlePassageVerified({ passage }: { passage: any }) {
      // Update enrichedPassages if the verified passage exists in our list
      const idx = this.enrichedPassages.findIndex(
        (p) => p.passageId === passage.passageId
      );
      if (idx !== -1) {
        this.enrichedPassages[idx].passageWaveforms = passage.passageWaveforms;
      }
      // Update decodedWaveforms if the current passage is the one that changed
      if (this.currentPassage?.passageId === passage.passageId) {
        let verifiedId: number | null = null;
        for (const dw of this.decodedWaveforms) {
          const updatedWf = (passage.passageWaveforms || []).find(
            (wf: any) => wf.passageWaveformId === dw.passageWaveformId
          );
          if (updatedWf) {
            dw.isVerified = updatedWf.isVerified;
            if (updatedWf.isVerified) {
              verifiedId = dw.passageWaveformId;
            }
          }
        }
        // Highlight the verified waveform card (or clear if unverified)
        this.selectedWaveformId = verifiedId;
      }
    },

    // ─── SignalR subscription ─────────────────────────────────────────

    subscribeToSignalR() {
      if (this.unsubscribeAction) return; // already subscribed
      this.unsubscribeAction = this.$store.subscribeAction((action: any) => {
        if (action.type !== 'mlResultReceived') return;
        // Guard: only refresh if a pig run is selected (panel is fully loaded).
        if (this.selectedPigRunId == null) return;

        const results: any[] = action.payload?.results ?? [];
        // If the backend includes confidence data on each result, only refresh
        // for results that cross the current threshold.
        // If the payload items don't carry those fields, refresh unconditionally
        // so we don't silently miss updates.
        const hasConfidenceData = results.some(
          (r) => r.mlConfidence != null && r.mlPrediction != null
        );
        if (hasConfidenceData) {
          const hasHighConfidence = results.some(
            (r) =>
              r.mlPrediction === true &&
              r.mlConfidence >= this.mlConfidenceThreshold
          );
          if (!hasHighConfidence) return;
        }

        this.scheduleRefresh();
      });
    },

    scheduleRefresh() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      // Fire the detection animation immediately so the user gets instant
      // visual feedback, then fetch after the debounce window closes.
      this.triggerDetectionAnimation();
      // 3 s debounce: batches rapid ML result bursts into a single fetch.
      this.debounceTimer = setTimeout(() => {
        this.flushScheduledRefresh();
      }, 3000);
      // Max-wait guarantee: if ML results keep arriving, force a
      // fetch after 8 seconds so the panel never goes stale.
      if (!this.refreshMaxWaitTimer) {
        this.refreshMaxWaitTimer = setTimeout(() => {
          this.flushScheduledRefresh();
        }, 8000);
      }
    },

    flushScheduledRefresh() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      if (this.refreshMaxWaitTimer) {
        clearTimeout(this.refreshMaxWaitTimer);
        this.refreshMaxWaitTimer = null;
      }
      if (this.visible) this.loadMLPassages(true);
    },

    triggerDetectionAnimation() {
      if (this.detectionResetTimer) clearTimeout(this.detectionResetTimer);
      this.newDetection = true;
      // Match the animation duration defined in CSS (3 s × 1 iteration)
      this.detectionResetTimer = setTimeout(() => {
        this.newDetection = false;
        this.detectionResetTimer = null;
      }, 3000);
    },

    // ─── Cleanup ──────────────────────────────────────────────────────

    cleanup() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      if (this.refreshMaxWaitTimer) {
        clearTimeout(this.refreshMaxWaitTimer);
        this.refreshMaxWaitTimer = null;
      }
      if (this.detectionResetTimer) {
        clearTimeout(this.detectionResetTimer);
        this.detectionResetTimer = null;
      }
      this.newDetection = false;
      if (this.unsubscribeAction) {
        this.unsubscribeAction();
        this.unsubscribeAction = null;
      }
      this.enrichedPassages = [];
      this.decodedWaveforms = [];
      this.currentIndex = 0;
      this.selectedWaveformId = null;
      this.loading = false;
      this.waveformLoading = false;
    },

    // ─── Helpers ──────────────────────────────────────────────────────

    pigRunLabel(entry: PigRunMapEntry): string {
      const toolName = entry.pigRun.tools?.[0]?.name;
      return toolName
        ? `${toolName} on ${entry.pigRun.name}`
        : entry.pigRun.name;
    },

    mlWaveforms(passage: IPassage): IPassageWaveformSummary[] {
      return (passage.passageWaveforms || []).filter(
        (wf) => wf.mlPrediction != null && wf.mlConfidence != null
      );
    },

    getMLMean(prediction: boolean, confidence: number): number {
      return (2 * Number(prediction) - 1) * confidence;
    },

    getMLColor(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      if (mean > 0.89) return 'green';
      if (mean > 0.75) return 'green-4';
      if (mean > -0.25) return 'orange';
      if (mean > -0.5) return 'red-3';
      return 'red';
    },

    getMLConfidenceLabel(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      return (50 * mean + 50).toFixed(1) + '%';
    },

    getWaveformTypeLabel(type: string): string {
      return WaveformTypeLabel[type] || type;
    },

    formatTimestamp(ts: string): string {
      if (!ts) return '—';
      try {
        const utcTs =
          ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts) ? ts : ts + 'Z';
        return new Date(utcTs).toUTCString().replace(' GMT', ' UTC');
      } catch {
        return ts;
      }
    },
  },
});
</script>

<style scoped>
.ml-passages-panel {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: fit-content;
  min-width: 360px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.22);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.ml-panel--new-detection {
  animation: ml-detection-sparkle 3s ease-out forwards;
}

@keyframes ml-detection-sparkle {
  0% {
    box-shadow: 0 0 0 3px #ff0080, 0 0 14px 7px rgba(255, 0, 128, 0.55),
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
  17% {
    box-shadow: 0 0 0 3px #ff6600, 0 0 14px 7px rgba(255, 102, 0, 0.55),
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
  33% {
    box-shadow: 0 0 0 3px #f5d800, 0 0 14px 7px rgba(245, 216, 0, 0.55),
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
  50% {
    box-shadow: 0 0 0 3px #00cc55, 0 0 14px 7px rgba(0, 204, 85, 0.55),
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
  67% {
    box-shadow: 0 0 0 3px #0099ff, 0 0 14px 7px rgba(0, 153, 255, 0.55),
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
  83% {
    box-shadow: 0 0 0 3px #9900ff, 0 0 14px 7px rgba(153, 0, 255, 0.4),
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
  100% {
    box-shadow: 0 0 0 0 transparent, 0 0 0 0 transparent,
      0 4px 24px rgba(0, 0, 0, 0.22);
  }
}

.ml-panel-header {
  background: #24337a;
  padding: 10px 14px 8px;
  flex-shrink: 0;
}

.ml-panel-body {
  /* No overflow — everything must fit without scrolling */
  overflow: hidden;
  /* Prevent panel from shrinking during background refreshes */
  min-height: 280px;
}

.ml-breadcrumb-btn {
  padding: 0 4px;
  min-height: unset;
  line-height: 1.2;
}

.ml-waveform-row {
  flex-wrap: nowrap;
}

.ml-waveform-card {
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 6px;
  background: #fafafa;
  min-width: 176px;
  flex-shrink: 0;
}

.ml-waveform-selectable {
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.ml-waveform-selectable:hover {
  border-color: #90caf9;
}

.ml-waveform-selected {
  border-color: #24337a;
  box-shadow: 0 0 0 2px rgba(36, 51, 122, 0.25);
  background: #eceef5;
}

.ml-waveform-canvas {
  display: block;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}
</style>
